import { lazy } from "react"

/**
 * Resilient replacement for React.lazy.
 *
 * Code-split routes are fetched on demand, so a single dropped/stalled request for one
 * `static/js/<id>.<hash>.chunk.js` is enough to break a navigation. Webpack surfaces that as a
 * ChunkLoadError, which bubbles to the router's errorElement and shows the generic error page.
 *
 * This wrapper recovers from the two failure modes we actually see in production:
 *   1. A transient stall/drop  -> one short-backoff retry silently fixes it.
 *   2. A stale index.html after a deploy (the document references chunk hashes that no longer
 *      exist) -> no amount of retrying helps, but a single full reload fetches a fresh index.
 *
 * The reload is hard-capped to once per RELOAD_GUARD_WINDOW_MS via sessionStorage so a genuinely
 * broken deploy degrades to the error page instead of an infinite reload loop.
 */

const RELOAD_GUARD_KEY = "ct.chunkReloadedAt"
const RELOAD_GUARD_WINDOW_MS = 5 * 60 * 1000
const RETRY_DELAY_MS = 500
// If location.reload() somehow does not navigate away, fall back to the error page rather than
// leaving the user on an indefinite Suspense spinner (which is the bug we are fixing).
const RELOAD_ESCAPE_HATCH_MS = 5000

// Webpack, Safari and Firefox all word "the dynamic import failed" differently.
const CHUNK_ERROR_PATTERN =
  /Loading chunk .* failed|Loading CSS chunk .* failed|Importing a module script failed|error loading dynamically imported module|Failed to fetch dynamically imported module/i

/**
 * Single source of truth for "is this a code-split asset that failed to load?".
 * Shared with the error page so the friendly copy and the self-heal logic can never disagree.
 */
export function isChunkLoadError(error) {
  if (!error) return false
  if (error.name === "ChunkLoadError") return true
  const message = typeof error === "string" ? error : (error.message ?? error.statusText ?? "")
  if (typeof message !== "string" || message.length === 0) return false
  return CHUNK_ERROR_PATTERN.test(message)
}

function getSessionStorage() {
  try {
    if (typeof window === "undefined") return null
    return window.sessionStorage ?? null
  } catch {
    // Accessing sessionStorage can itself throw (Safari private mode, blocked third-party storage).
    return null
  }
}

function readReloadGuard() {
  try {
    const storage = getSessionStorage()
    if (!storage) return 0
    const parsed = Number(storage.getItem(RELOAD_GUARD_KEY))
    return Number.isFinite(parsed) ? parsed : 0
  } catch {
    return 0
  }
}

/**
 * Records that we are about to reload. Returns false when the guard could not be persisted —
 * in that case we deliberately do NOT reload, because without a durable marker we could loop.
 */
function markReloadAttempt(now) {
  try {
    const storage = getSessionStorage()
    if (!storage) return false
    storage.setItem(RELOAD_GUARD_KEY, String(now))
    return true
  } catch {
    return false
  }
}

function clearReloadGuard() {
  try {
    const storage = getSessionStorage()
    if (!storage) return
    storage.removeItem(RELOAD_GUARD_KEY)
  } catch {
    // Best effort only; a stale guard just means we skip one future self-heal.
  }
}

function canReloadNow(now) {
  const lastAttempt = readReloadGuard()
  if (lastAttempt > 0 && now - lastAttempt < RELOAD_GUARD_WINDOW_MS) return false
  return typeof window !== "undefined" && typeof window.location?.reload === "function"
}

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Wraps a dynamic-import factory with retry + one-time reload recovery.
 * Exported separately so it can be unit-tested without going through React.lazy.
 */
export function importWithRetry(importer, options = {}) {
  const retryDelayMs = options.retryDelayMs ?? RETRY_DELAY_MS
  const reloadEscapeHatchMs = options.reloadEscapeHatchMs ?? RELOAD_ESCAPE_HATCH_MS

  return async () => {
    try {
      const loaded = await importer()
      // A successful load means the app is healthy again, so a later unrelated failure is
      // still allowed to self-heal.
      clearReloadGuard()
      return loaded
    } catch {
      await delay(retryDelayMs)
      try {
        const loaded = await importer()
        clearReloadGuard()
        return loaded
      } catch (retryError) {
        const now = Date.now()
        if (isChunkLoadError(retryError) && canReloadNow(now) && markReloadAttempt(now)) {
          window.location.reload()
          // Stay pending so Suspense keeps showing the loader while the browser navigates,
          // instead of flashing the error page. The escape hatch bounds the wait.
          return await new Promise((resolve, reject) => {
            setTimeout(() => reject(retryError), reloadEscapeHatchMs)
          })
        }
        throw retryError
      }
    }
  }
}

/**
 * Drop-in replacement for React.lazy.
 */
export default function lazyWithRetry(importer, options) {
  return lazy(importWithRetry(importer, options))
}
